import { LitElement, css } from 'lit';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { InvBool, parseVec3, findAncestor } from './base.js';

export class ThreeOrbitControls extends LitElement {
  static styles = css`:host { display: contents; }`;

  static properties = {
    enabled: { converter: InvBool, reflect: true },
    damping: { converter: InvBool, reflect: true },
    zoom: { converter: InvBool, reflect: true },
    pan: { converter: InvBool, reflect: true },
    rotate: { converter: InvBool, reflect: true },
    target: { type: String },
    minDistance: { type: Number, attribute: 'min-distance' },
    maxDistance: { type: Number, attribute: 'max-distance' },
  };

  constructor() {
    super();
    this.enabled = true;
    this.damping = true;
    this.zoom = true;
    this.pan = true;
    this.rotate = true;
    this.target = '0 0 0';
    this.minDistance = 0;
    this.maxDistance = Infinity;
    this.controls = null;
    this._scene = null;
    this._tick = null;
  }

  async connectedCallback() {
    super.connectedCallback();
    const sceneEl = findAncestor(this, (el) => el.tagName === 'THREE-SCENE');
    if (!sceneEl) return;
    this._scene = sceneEl;
    if (sceneEl.whenReady) await sceneEl.whenReady;
    if (!this.isConnected) return;
    this._setup();
  }

  _setup() {
    const cam = this._scene.getActiveCamera();
    const dom = this._scene.getRenderer().domElement;
    this.controls = new OrbitControls(cam, dom);
    this._applyAll();
    this._tick = () => this.controls.update();
    this._scene.addFrameCallback(this._tick);
    // Listen for drag-lock events from interactive uikit widgets
    // (webgl-slider, future webgl-color-picker, gizmo handles).
    // While a widget owns the pointer, freeze orbit-controls so
    // dragging the widget doesn't also rotate/pan the camera.
    this._dragStartHandler = () => {
      if (!this.controls) return;
      this._wasEnabled = this.controls.enabled;
      this.controls.enabled = false;
    };
    this._dragEndHandler = () => {
      if (!this.controls) return;
      if (this._wasEnabled == null) return;
      this.controls.enabled = this._wasEnabled;
      this._wasEnabled = null;
    };
    this._scene.addEventListener('webgl-drag-start', this._dragStartHandler);
    this._scene.addEventListener('webgl-drag-end', this._dragEndHandler);
  }

  _applyAll() {
    const c = this.controls;
    if (!c) return;
    c.enabled = this.enabled;
    c.enableDamping = this.damping;
    c.enableZoom = this.zoom;
    c.enablePan = this.pan;
    c.enableRotate = this.rotate;
    c.minDistance = this.minDistance;
    c.maxDistance = this.maxDistance;
    const [x, y, z] = parseVec3(this.target);
    c.target.set(x, y, z);
  }

  updated(changed) {
    if (!this.controls) return;
    if (changed.has('enabled')) this.controls.enabled = this.enabled;
    if (changed.has('damping')) this.controls.enableDamping = this.damping;
    if (changed.has('zoom')) this.controls.enableZoom = this.zoom;
    if (changed.has('pan')) this.controls.enablePan = this.pan;
    if (changed.has('rotate')) this.controls.enableRotate = this.rotate;
    if (changed.has('minDistance')) this.controls.minDistance = this.minDistance;
    if (changed.has('maxDistance')) this.controls.maxDistance = this.maxDistance;
    if (changed.has('target')) {
      const [x, y, z] = parseVec3(this.target);
      this.controls.target.set(x, y, z);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._scene && this._tick) this._scene.removeFrameCallback(this._tick);
    if (this._scene && this._dragStartHandler) {
      this._scene.removeEventListener('webgl-drag-start', this._dragStartHandler);
      this._scene.removeEventListener('webgl-drag-end', this._dragEndHandler);
    }
    if (this.controls) this.controls.dispose();
    this.controls = null;
  }
}

customElements.define('three-orbit-controls', ThreeOrbitControls);
